import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { LocationsService } from '../../core/services/locations.services';
import * as LocationsActions from './locations.actions';

@Injectable()
export class LocationsEffects {
  private actions$ = inject(Actions);
  private locationsService = inject(LocationsService);

  // Load all provinces
  loadProvinces$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.loadProvinces),
      switchMap(() =>
        this.locationsService.getProvinces().pipe(
          map((response) => {
            const provinces = response.payload || [];
            return LocationsActions.loadProvincesSuccess({ provinces });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(
              LocationsActions.loadProvincesFailure({ error: errorMessage })
            );
          })
        )
      )
    )
  );

  // Load all districts
  loadDistricts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.loadDistricts),
      switchMap(() =>
        this.locationsService.getDistricts().pipe(
          map((response) => {
            const districts = response.payload || [];
            return LocationsActions.loadDistrictsSuccess({ districts });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(
              LocationsActions.loadDistrictsFailure({ error: errorMessage })
            );
          })
        )
      )
    )
  );

  // Load districts by province
  loadDistrictsByProvince$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.loadDistrictsByProvince),
      switchMap((action) =>
        this.locationsService.getDistrictsByProvince(action.provinceId).pipe(
          map((response) => {
            const districts = response.payload.content;
            return LocationsActions.loadDistrictsByProvinceSuccess({ districts });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(
              LocationsActions.loadDistrictsByProvinceFailure({ error: errorMessage })
            );
          })
        )
      )
    )
  );

  // Load sectors by district
  loadSectors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.loadSectors),
      switchMap((action) =>
        this.locationsService.getSectors(action.districtId).pipe(
          map((response) => {
            const sectors = response.payload.content || [];
            return LocationsActions.loadSectorsSuccess({ sectors });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(LocationsActions.loadSectorsFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Load cells by sector
  loadCells$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.loadCells),
      switchMap((action) =>
        this.locationsService.getCells(action.sectorId).pipe(
          map((response) => {
            const cells = response.payload.content || [];
            return LocationsActions.loadCellsSuccess({ cells });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(
              LocationsActions.loadCellsFailure({ error: errorMessage })
            );
          })
        )
      )
    )
  );

  // Load villages by cell
  loadVillages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.loadVillages),
      switchMap((action) =>
        this.locationsService.getVillages(action.cellId).pipe(
          map((response) => {
            const villages = response.payload.content || [];
            return LocationsActions.loadVillagesSuccess({ villages });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(
              LocationsActions.loadVillagesFailure({ error: errorMessage })
            );
          })
        )
      )
    )
  );
}
